import { ShotData } from '../types';

export interface PlayerProfile {
  id: string;
  name: string;
  height: number; // cm
  weight: number; // kg
  armLength: number; // cm
  battingStyle: 'right' | 'left';
  experienceLevel: 'beginner' | 'intermediate' | 'advanced' | 'professional';
  strengths: string[];
  weaknesses: string[];
  averageBatSpeed: number; // m/s
  averagePower: number; // 0-1
  preferredShotTypes: string[];
}

export interface OptimalShotParameters {
  batSpeed: number;
  impactLocation: { x: number; y: number; z: number };
  launchAngle: number;
  horizontalAngle: number;
  timing: number; // ms relative to perfect timing
  bodyPosition: {
    stance: string;
    backLift: number;
    followThrough: number;
  };
  predictedDistance: number;
  predictedQuality: string;
  confidence: number; // 0-1
}

export interface OptimizationResult {
  current: ShotData;
  optimal: OptimalShotParameters;
  improvements: {
    parameter: string;
    currentValue: number;
    optimalValue: number;
    improvement: number;
    priority: 'high' | 'medium' | 'low';
  }[];
  recommendations: string[];
  potentialGain: {
    distance: number;
    power: number;
    consistency: number;
  };
}

export class PerfectShotOptimizer {
  private playerProfile: PlayerProfile | null = null;
  private historicalShots: ShotData[] = [];
  private optimalParameters: Map<string, OptimalShotParameters> = new Map();
  
  /**
   * Initialize optimizer with player profile
   */
  constructor(profile?: PlayerProfile) {
    if (profile) {
      this.playerProfile = profile;
    }
  }

  /**
   * Set or update player profile
   */
  setPlayerProfile(profile: PlayerProfile) {
    this.playerProfile = profile;
    this.recalculateOptimalParameters();
  }

  /**
   * Add historical shots for learning
   */
  addHistoricalShots(shots: ShotData[]) {
    this.historicalShots.push(...shots);
    this.recalculateOptimalParameters();
  }

  /**
   * Calculate optimal shot parameters for a given shot type
   */
  calculateOptimalParameters(shotType: string): OptimalShotParameters {
    // Check if we have cached optimal parameters
    if (this.optimalParameters.has(shotType)) {
      return this.optimalParameters.get(shotType)!;
    }

    // Base optimal parameters (can be refined with ML)
    const baseOptimal: OptimalShotParameters = {
      batSpeed: 35, // m/s - typical optimal bat speed
      impactLocation: { x: 0, y: 0, z: 0 }, // Sweet spot
      launchAngle: 25, // degrees - optimal for distance
      horizontalAngle: 0, // straight
      timing: 0, // perfect timing
      bodyPosition: {
        stance: 'balanced',
        backLift: 45, // degrees
        followThrough: 180 // degrees
      },
      predictedDistance: 75, // meters
      predictedQuality: 'EXCELLENT',
      confidence: 0.85
    };

    // Adjust based on player profile
    if (this.playerProfile) {
      baseOptimal.batSpeed = this.adjustForPlayerPhysique(baseOptimal.batSpeed);
      baseOptimal.launchAngle = this.adjustForShotType(shotType, baseOptimal.launchAngle);
      
      // Learn from historical shots
      if (this.historicalShots.length > 0) {
        const adjustments = this.learnFromHistory(shotType);
        baseOptimal.batSpeed = adjustments.batSpeed || baseOptimal.batSpeed;
        baseOptimal.launchAngle = adjustments.launchAngle || baseOptimal.launchAngle;
      }
    }

    // Calculate predicted outcome
    baseOptimal.predictedDistance = this.predictDistance(
      baseOptimal.batSpeed,
      baseOptimal.launchAngle,
      baseOptimal.impactLocation
    );

    // Cache the result
    this.optimalParameters.set(shotType, baseOptimal);
    
    return baseOptimal;
  }

  /**
   * Optimize a shot and provide improvement suggestions
   */
  optimizeShot(shot: ShotData): OptimizationResult {
    const shotType = shot.shotType || 'drive';
    const optimal = this.calculateOptimalParameters(shotType);
    
    // Calculate improvements needed
    const improvements = this.calculateImprovements(shot, optimal);
    
    // Generate recommendations
    const recommendations = this.generateRecommendations(shot, optimal, improvements);
    
    // Calculate potential gains
    const potentialGain = this.calculatePotentialGains(shot, optimal);
    
    return {
      current: shot,
      optimal,
      improvements,
      recommendations,
      potentialGain
    };
  }

  /**
   * Real-time optimization during shot
   */
  realTimeOptimization(
    currentBatSpeed: number,
    currentAngle: number,
    timeToImpact: number
  ): { adjustment: string; urgency: 'high' | 'medium' | 'low' } {
    const optimal = this.calculateOptimalParameters('drive');
    
    // Quick decision based on time to impact
    if (timeToImpact < 100) { // Less than 100ms to impact
      // Too late for major adjustments
      return {
        adjustment: 'Maintain current swing',
        urgency: 'low'
      };
    }
    
    // Speed adjustment
    const speedDiff = optimal.batSpeed - currentBatSpeed;
    if (Math.abs(speedDiff) > 5) {
      return {
        adjustment: speedDiff > 0 ? 'Accelerate bat speed' : 'Controlled swing',
        urgency: 'high'
      };
    }
    
    // Angle adjustment
    const angleDiff = optimal.launchAngle - currentAngle;
    if (Math.abs(angleDiff) > 10) {
      return {
        adjustment: angleDiff > 0 ? 'Lift bat angle' : 'Lower bat angle',
        urgency: 'medium'
      };
    }
    
    return {
      adjustment: 'Good position - follow through',
      urgency: 'low'
    };
  }

  /**
   * Calibrate optimizer for specific player
   */
  calibrateForPlayer(shots: ShotData[]): void {
    if (shots.length < 10) {
      throw new Error('Need at least 10 shots for calibration');
    }
    
    // Group shots by quality
    const excellentShots = shots.filter(s => s.quality === 'excellent');
    
    // Learn optimal parameters from best shots
    if (excellentShots.length > 0) {
      const avgBatSpeed = excellentShots.reduce((sum, s) => sum + (s.batSpeed || 0), 0) / excellentShots.length;
      const avgLaunchAngle = excellentShots.reduce((sum, s) => sum + (s.launchAngle || 0), 0) / excellentShots.length;
      
      // Update player profile
      if (this.playerProfile) {
        this.playerProfile.averageBatSpeed = avgBatSpeed;
      }
      
      // Update optimal parameters for all shot types
      this.optimalParameters.forEach((params, _shotType) => {
        params.batSpeed = avgBatSpeed * 1.05; // Aim for 5% improvement
        params.launchAngle = avgLaunchAngle;
      });
    }
  }

  /**
   * Private helper methods
   */
  
  private adjustForPlayerPhysique(baseBatSpeed: number): number {
    if (!this.playerProfile) return baseBatSpeed;
    
    // Adjust based on height and weight
    const heightFactor = this.playerProfile.height / 175; // Normalize to average height
    const weightFactor = this.playerProfile.weight / 75; // Normalize to average weight
    
    // Taller players can generate more leverage
    // Heavier players can generate more power
    return baseBatSpeed * (0.7 + 0.15 * heightFactor + 0.15 * weightFactor);
  }

  private adjustForShotType(shotType: string, baseLaunchAngle: number): number {
    const shotAdjustments: { [key: string]: number } = {
      'drive': 0,
      'pull': -5,
      'cut': -10,
      'sweep': -15,
      'loft': 10,
      'scoop': 15
    };
    
    return baseLaunchAngle + (shotAdjustments[shotType] || 0);
  }

  private learnFromHistory(shotType: string): Partial<OptimalShotParameters> {
    const relevantShots = this.historicalShots.filter(s => s.shotType === shotType);
    
    if (relevantShots.length === 0) {
      return {};
    }
    
    // Find the best performing shots
    const topShots = relevantShots
      .sort((a, b) => (b.distance || 0) - (a.distance || 0))
      .slice(0, Math.min(5, relevantShots.length));
    
    // Average their parameters
    const avgBatSpeed = topShots.reduce((sum, s) => sum + (s.batSpeed || 0), 0) / topShots.length;
    const avgLaunchAngle = topShots.reduce((sum, s) => sum + (s.launchAngle || 0), 0) / topShots.length;
    
    return {
      batSpeed: avgBatSpeed,
      launchAngle: avgLaunchAngle
    };
  }

  private predictDistance(batSpeed: number, launchAngle: number, impactLocation: { x: number; y: number; z: number }): number {
    // Simplified physics model
    const sweetSpotFactor = 1 - Math.sqrt(impactLocation.x ** 2 + impactLocation.y ** 2) / 100;
    const angleRad = (launchAngle * Math.PI) / 180;
    const exitVelocity = batSpeed * 1.5 * sweetSpotFactor; // Simplified coefficient
    
    // Realistic projectile motion with air resistance for cricket
    const g = 9.81;
    const vacuumDistance = (exitVelocity ** 2 * Math.sin(2 * angleRad)) / g;
    
    // Cricket physics: account for air resistance and real-world conditions
    const airResistanceFactor = 0.55; // 45% reduction from vacuum
    const realismFactor = 0.90; // Real-world conditions
    const distance = Math.min(120, vacuumDistance * airResistanceFactor * realismFactor);
    
    return Math.max(0, distance);
  }

  private calculateImprovements(current: ShotData, optimal: OptimalShotParameters) {
    const improvements = [];
    
    // Bat speed improvement
    const speedImprovement = optimal.batSpeed - (current.batSpeed || 0);
    if (Math.abs(speedImprovement) > 1) {
      improvements.push({
        parameter: 'Bat Speed',
        currentValue: current.batSpeed || 0,
        optimalValue: optimal.batSpeed,
        improvement: speedImprovement,
        priority: Math.abs(speedImprovement) > 5 ? 'high' as const : 'medium' as const
      });
    }
    
    // Launch angle improvement
    const angleImprovement = optimal.launchAngle - (current.launchAngle || 0);
    if (Math.abs(angleImprovement) > 2) {
      improvements.push({
        parameter: 'Launch Angle',
        currentValue: current.launchAngle || 0,
        optimalValue: optimal.launchAngle,
        improvement: angleImprovement,
        priority: Math.abs(angleImprovement) > 10 ? 'high' as const : 'medium' as const
      });
    }
    
    // Sweet spot improvement
    const sweetSpotDistance = current.sweetSpotDistance || 0;
    if (sweetSpotDistance > 5) {
      improvements.push({
        parameter: 'Sweet Spot Contact',
        currentValue: sweetSpotDistance,
        optimalValue: 0,
        improvement: -sweetSpotDistance,
        priority: sweetSpotDistance > 15 ? 'high' as const : 'medium' as const
      });
    }
    
    return improvements;
  }

  private generateRecommendations(
    current: ShotData,
    _optimal: OptimalShotParameters,
    improvements: any[]
  ): string[] {
    const recommendations: string[] = [];
    
    // Prioritize high-priority improvements
    const highPriority = improvements.filter(i => i.priority === 'high');
    
    highPriority.forEach(improvement => {
      switch (improvement.parameter) {
        case 'Bat Speed':
          if (improvement.improvement > 0) {
            recommendations.push('Increase bat speed through explosive hip rotation and stronger grip');
          } else {
            recommendations.push('Control bat speed for better timing and contact');
          }
          break;
        
        case 'Launch Angle':
          if (improvement.improvement > 0) {
            recommendations.push('Elevate bat path slightly for optimal launch angle');
          } else {
            recommendations.push('Keep bat path more level to reduce excessive loft');
          }
          break;
        
        case 'Sweet Spot Contact':
          recommendations.push('Focus on watching the ball onto the bat for better contact');
          break;
      }
    });
    
    // Add timing recommendation if needed
    if (current.timing === 'poor' || current.timing === 'fair') {
      recommendations.push('Work on timing drills to improve contact point consistency');
    }
    
    // Add general improvement tips
    if (recommendations.length === 0) {
      recommendations.push('Good technique! Focus on consistency and repetition');
    }
    
    return recommendations;
  }

  private calculatePotentialGains(current: ShotData, optimal: OptimalShotParameters) {
    const currentDistance = current.distance || 0;
    const potentialDistance = optimal.predictedDistance;
    
    const currentPower = current.powerTransfer || 0;
    const potentialPower = 0.9; // Assume 90% power transfer is achievable
    
    // Consistency score based on how close current is to optimal
    const parameterDifferences = [
      Math.abs((current.batSpeed || 0) - optimal.batSpeed) / optimal.batSpeed,
      Math.abs((current.launchAngle || 0) - optimal.launchAngle) / optimal.launchAngle,
      (current.sweetSpotDistance || 0) / 20 // Normalize to 0-1
    ];
    
    const avgDifference = parameterDifferences.reduce((a, b) => a + b, 0) / parameterDifferences.length;
    const consistencyGain = (1 - avgDifference) * 100;
    
    return {
      distance: potentialDistance - currentDistance,
      power: (potentialPower - currentPower) * 100,
      consistency: consistencyGain
    };
  }

  private recalculateOptimalParameters() {
    // Clear cache to force recalculation
    this.optimalParameters.clear();
  }

  /**
   * Export optimizer configuration
   */
  exportConfiguration(): string {
    return JSON.stringify({
      playerProfile: this.playerProfile,
      optimalParameters: Array.from(this.optimalParameters.entries()),
      historicalShotsCount: this.historicalShots.length
    }, null, 2);
  }

  /**
   * Import optimizer configuration
   */
  importConfiguration(config: string) {
    const data = JSON.parse(config);
    this.playerProfile = data.playerProfile;
    this.optimalParameters = new Map(data.optimalParameters);
    // Note: Historical shots are not imported for privacy reasons
  }
}

// Export singleton instance
export const perfectShotOptimizer = new PerfectShotOptimizer();